import { useState } from "react";
import { Download, FileDown } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FORMSPREE_ENDPOINT, BROCHURE_URL } from "@/lib/site";

// Reusable button that opens a modal capturing Name + Email, then downloads the brochure PDF.
export default function BrochureButton({ className = "", label = "Download Brochure", variant = "solid" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = BROCHURE_URL;
    a.download = "FlyGowell-Corporate-Travel-Brochure.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    // Capture the lead (best-effort); download proceeds regardless.
    try {
      await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...form, _subject: "Brochure Download: Fly Gowell" }),
      });
    } catch {
      /* ignore — still let the user download */
    }
    triggerDownload();
    trackEvent("generate_lead", { form: "brochure" });
    trackEvent("file_download", { file_name: "FlyGowell-Corporate-Travel-Brochure.pdf" });
    toast.success("Thanks! Your brochure is downloading.");
    setForm({ name: "", email: "" });
    setSending(false);
    setOpen(false);
  };

  const base = "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all";
  const styles =
    variant === "outline"
      ? "border-2 border-white/40 text-white hover:bg-white hover:text-brand-blue"
      : "bg-brand-yellow text-brand-blue hover:brightness-105";

  return (
    <>
      <button
        type="button"
        data-testid="brochure-open-btn"
        onClick={() => setOpen(true)}
        className={`${base} ${styles} ${className}`}
      >
        <FileDown size={18} /> {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="brochure-modal">
          <DialogHeader>
            <div className="inline-flex w-fit rounded-xl bg-gradient-to-br from-brand-blue to-brand-green p-3 text-white">
              <Download size={22} />
            </div>
            <DialogTitle className="font-heading text-2xl text-brand-blue mt-3">
              Download Corporate Travel Brochure
            </DialogTitle>
            <DialogDescription className="text-brand-slate">
              Enter your details and we&apos;ll start your download instantly.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} data-testid="brochure-form" className="mt-2 space-y-5">
            <div>
              <Label className="text-brand-slate">Name</Label>
              <Input data-testid="brochure-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-brand-slate">Email</Label>
              <Input data-testid="brochure-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className="mt-1.5" />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="brochure-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-3.5 font-semibold text-white hover:bg-brand-ocean transition-colors disabled:opacity-60"
            >
              <Download size={17} /> {sending ? "Preparing..." : "Download Brochure"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
